// @tenorlab/nuxt-dashboard-pro
// file: app/state/navigation-store/navigation.store.ts
import { reactive, computed } from 'vue'
import { navigationItemsStrategy, getSecondaryNavigationItems } from '@/models/navigation/strategies'
import { sortDashboardConfigs } from '@/utils'
import { AppMenuTypeEnum, filterMenuItems, menuItemsCatalog } from '@/models'
import type { INavigationItem } from '@/models'
import type { IDashboardConfig } from '@tenorlab/vue-dashboard'

export interface INavigationState {
  isLoading: boolean
  isMobileSidebarOpen: boolean
  isDesktopSidebarCollapsed: boolean
  dashboardMenuItem: INavigationItem
  navigationItems: INavigationItem[]
  secondaryNavigationItems: INavigationItem[]
  currentNavigationItem: INavigationItem | undefined
}

// --- HELPERS (Pure Logic) ---
const _getCurrentNavigationItem = (items: INavigationItem[]): INavigationItem | undefined => {
  for (const item of items) {
    if (item) {
      if (item.selected) return item
      // Recursively check children
      if (item.children) {
        const found = _getCurrentNavigationItem(item.children)
        if (found) return found
      }
    }
  }
  return undefined
}

const _selectMenuItemById = (items: INavigationItem[], id: string): INavigationItem[] => {
  return items.map((item) => {
    const updatedChildren = (item.children || []).map((child) => ({
      ...child,
      selected: child.id === id,
    }))
    const hasSelectedChild = updatedChildren.some((c) => c.selected)
    const isParentTarget = item.id === id

    return {
      ...item,
      selected: isParentTarget,
      isExpanded: hasSelectedChild ? true : item.isExpanded,
      children: updatedChildren,
    }
  })
}

const _expandOrCollapseNavigationItem = (
  items: INavigationItem[],
  targetItem: INavigationItem,
): INavigationItem[] => {
  return items.map((item) => {
    if (item.id === targetItem.id) {
      const newIsExpanded = !item.isExpanded
      const children = item.children || []

      if (newIsExpanded && children.length > 0) {
        return {
          ...item,
          isExpanded: newIsExpanded,
          children: children.map((c, idx) => ({ ...c, selected: idx === 0 })),
          selected: false,
        }
      }
      return { ...item, isExpanded: newIsExpanded, selected: false }
    }
    return { ...item, isExpanded: false, selected: false }
  })
}

/**
 * @name computeDashboardMenu
 * @description Helper to compute the Dashboards menu model
 * @param dashboardMenuItem: the current dashbaord menu item (usually from state)
 * @param dashboardConfigs: the current list of custom dashboards
 * @param selectedId: the id of the selected menu item
 * @returns The update instance of the dashboardMenuItem with the refreshed children
 */
const computeDashboardMenu = (
  dashboardMenuItem: INavigationItem,
  dashboardConfigs: IDashboardConfig[],
  selectedId?: string,
): INavigationItem => {
  const dashboardsMenuId = dashboardMenuItem.id
  // compute children (one child menu item per dashboard):
  const dynamicChildren: INavigationItem[] = sortDashboardConfigs([...dashboardConfigs]).map((x) => ({
    id: x.dashboardId as any,
    parentId: dashboardsMenuId,
    name: x.dashboardName,
    icon: 'Star',
    selected: x.dashboardId === selectedId,
    isExpanded: false,
    hidden: false,
  }))

  const childrenCount = dynamicChildren.length
  dashboardMenuItem.badgeValue = `${childrenCount}`
  dashboardMenuItem.badgeCategory = 'primary'

  dashboardMenuItem.children = dynamicChildren
  // Parent is expanded if one of its children is selected
  // dashboardMenuItem.isExpanded = dynamicChildren.some((c) => c.selected) || dashboardMenuItem.isExpanded
  dashboardMenuItem.isExpanded = true
  return dashboardMenuItem
}

const _state: INavigationState = reactive({
  isLoading: true, // Start false, or set true if you call initState on load
  isMobileSidebarOpen: false,
  isDesktopSidebarCollapsed: false,
  dashboardMenuItem: menuItemsCatalog.dashboardsMenu,
  navigationItems: [...(navigationItemsStrategy.get(AppMenuTypeEnum.full) || [])],
  secondaryNavigationItems: getSecondaryNavigationItems(),
  currentNavigationItem: undefined,
})

//
const _actions = {
  initState: async (userDashboardConfigs: IDashboardConfig[]) => {
    // Find if something was already selected
    const dashboardMenuItem = _state.dashboardMenuItem
    const navigationItems = [..._state.navigationItems]
    const secondaryNavigationItems = [..._state.secondaryNavigationItems]

    const updatedDashboardMenuItem = computeDashboardMenu(dashboardMenuItem, userDashboardConfigs)

    const currentNavigationItem = _getCurrentNavigationItem([
      updatedDashboardMenuItem,
      ...navigationItems,
      ...secondaryNavigationItems,
    ])

    // mutate
    _state.dashboardMenuItem = updatedDashboardMenuItem
    _state.currentNavigationItem = currentNavigationItem
    _state.isLoading = false
  },

  selectMenuItemById: async (id: string, updatedDashboardConfigs: IDashboardConfig[] = []) => {
    _state.isLoading = true
    let dashboardMenuItem = _state.dashboardMenuItem
    const navigationItems = [..._state.navigationItems]
    const secondaryNavigationItems = [..._state.secondaryNavigationItems]

    if (updatedDashboardConfigs.length > 0) {
      dashboardMenuItem = computeDashboardMenu(dashboardMenuItem, updatedDashboardConfigs)
    }

    // select
    const updatedDashboardMenuItem = _selectMenuItemById([dashboardMenuItem], id)[0]!
    const updatedNavigationItems = _selectMenuItemById(navigationItems, id)
    const updatedSecondaryNavigationItems = _selectMenuItemById(secondaryNavigationItems, id)

    const currentNavigationItem = _getCurrentNavigationItem([
      updatedDashboardMenuItem,
      ...updatedNavigationItems,
      ...updatedSecondaryNavigationItems,
    ])

    // mutate
    _state.dashboardMenuItem = updatedDashboardMenuItem
    _state.navigationItems = updatedNavigationItems
    _state.secondaryNavigationItems = updatedSecondaryNavigationItems
    _state.currentNavigationItem = currentNavigationItem
    _state.isLoading = false
  },

  expandOrCollapseNavigationItem: async (item: INavigationItem) => {
    _state.isLoading = true
    const dashboardMenuItem = _state.dashboardMenuItem
    const navigationItems = [..._state.navigationItems]
    const secondaryNavigationItems = [..._state.secondaryNavigationItems]

    const updatedDashboardMenuItem = _expandOrCollapseNavigationItem([dashboardMenuItem], item)[0]!
    const updatedNavigationItems = _expandOrCollapseNavigationItem(navigationItems, item)
    const updatedSecondaryNavigationItems = _expandOrCollapseNavigationItem(secondaryNavigationItems, item)

    const currentNavigationItem = _getCurrentNavigationItem([
      updatedDashboardMenuItem,
      ...updatedNavigationItems,
      ...updatedSecondaryNavigationItems,
    ])

    // mutate
    _state.dashboardMenuItem = updatedDashboardMenuItem
    _state.navigationItems = updatedNavigationItems
    _state.secondaryNavigationItems = updatedSecondaryNavigationItems
    _state.currentNavigationItem = currentNavigationItem
    _state.isLoading = false
  },

  toggleDesktopSidebarCollapsed: async () => {
    _state.isDesktopSidebarCollapsed = !_state.isDesktopSidebarCollapsed
  },

  toggleMobileSidebarOpen: async () => {
    _state.isMobileSidebarOpen = !_state.isMobileSidebarOpen
  },
}

const _computedGetters = {
  isLoading: computed(() => _state.isLoading),
  isDesktopSidebarCollapsed: computed(() => _state.isDesktopSidebarCollapsed),
  isMobileSidebarOpen: computed(() => _state.isMobileSidebarOpen),
  dashboardMenuItem: computed(() => _state.dashboardMenuItem),
  navigationItems: computed(() => {
    const items = filterMenuItems(_state.navigationItems || [])
    return items
  }),
  secondaryNavigationItems: computed(() => _state.secondaryNavigationItems),
  currentNavigationItem: computed(() => _state.currentNavigationItem),
}

type TActions = typeof _actions

export interface INavigationStore extends TActions {
  computed: typeof _computedGetters
}

const _instance: INavigationStore = {
  ..._actions,
  computed: _computedGetters,
}

export const useNavigationStore = (): INavigationStore => _instance
